import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { axios, APIBASE_REVIVERUSERS, APIBASE_REVIVERAPPLICATION } from "./ApiConfig";
import {
  AllPaymentOptions,
  Company,
  CreateUser,
  IndexRegulationPercentage,
  PaymentOptions,
  UpdateUser,
  UserRole,
} from "@/types";
import { AxiosResponse } from "axios";
import {
  UpdateAddressInput,
  AddressFormInput,
} from "@/components/pages/Settings/validation/addressValidationSchema";

export const createUser = async (user: CreateUser) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/user/create/advanced`, user);

export const deleteTeamMember = async (companyId: any, userId: any) =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companyId}/team/delete/${userId}`);

export const postChangeRole = async (userId: number, userRoleDto: UserRole) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/user/role`, {
    userId: userId,
    userRoleDto,
  });

export const updateUser = async (req: UpdateUser) => axios.put(`${APIBASE_REVIVERUSERS}api/user/update`, req);

export const updateCompany = async (company: Company) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/update`, company);

export const postNewAddress = async (companyId: number, address: AddressFormInput): Promise<AxiosResponse<void>> =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/${companyId}/address`, {
    ...address,
    countryId: Number(address.countryId),
  });

export const updateAddress = async (
  companyId: number,
  address: UpdateAddressInput
): Promise<AxiosResponse<void>> => axios.put(`${APIBASE_REVIVERUSERS}api/company/${companyId}/address/update`, address);

export const deleteAddress = async (companyId: number, addressId: number): Promise<AxiosResponse<void>> =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companyId}/address/${addressId}/delete`);

export const postNewBankAccount = async (companyId: number, name: string, accountNo: string, iban: string) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/${companyId}/account`, {
    name,
    accountNo,
    iban,
  });

export const updateBankAccount = async (
  companyId: number,
  id: number,
  name: string,
  accountNo: string,
  iban: string
): Promise<AxiosResponse<void>> =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/${companyId}/account/update`, {
    id,
    name,
    accountNo,
    iban,
  });

export const deleteBankAccount = async (companyId: number, accountId: number) =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companyId}/account/${accountId}/delete`);

const getPaymentOptions = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/payments/options`);

export const useGetPaymentOptions = (options?: Partial<UseQueryOptions<AllPaymentOptions>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<AllPaymentOptions>({
    queryKey: ["getPaymentOptions"],
    queryFn: async () => {
      const result = await getPaymentOptions();
      return result.data;
    },
    ...queryOptions,
  });
};

const getPaymentOptionsByCompanyId = async (companyId: any) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/payments/options/${companyId}`);

export const useGetPaymentOptionsByCompanyId = (
  companyId: number,
  options?: Partial<UseQueryOptions<PaymentOptions>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<PaymentOptions>({
    queryKey: ["getPaymentOptionsByCompanyId", companyId],
    queryFn: async () => {
      const result = await getPaymentOptionsByCompanyId(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getPaymentOptionsByCompanyIdAndSalesItem = async (companyId: number, salesItemId: number) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/payments/options/${companyId}/${salesItemId}`);

export const useGetPaymentOptionsByCompanyIdAndSalesItem = (
  companyId: number,
  salesItemId: number,
  options?: Partial<UseQueryOptions<PaymentOptions>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<PaymentOptions>({
    queryKey: ["getPaymentOptionsByCompanyIdAndSalesItem", companyId, salesItemId],
    queryFn: async () => {
      const result = await getPaymentOptionsByCompanyIdAndSalesItem(companyId, salesItemId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getCompanyIndexRegulationPercentage = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/settings/indexRegulationPercentage/${companyId}`);

export const useGetCompanyIndexRegulationPercentage = (
  companyId: number,
  options?: Partial<UseQueryOptions<IndexRegulationPercentage>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<IndexRegulationPercentage>({
    queryKey: ["getCompanyIndexRegulationPercentage", companyId],
    queryFn: async () => {
      const result = await getCompanyIndexRegulationPercentage(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const putUpdateCompanyIndexRegulationPercentage = async (companyId: number, percentage: number) =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/settings/indexRegulationPercentage/${companyId}`, {
    value: percentage,
  });
